import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { GptWrapper } from "../../core/GptWrapper";
import { cookieManager, getTopLevelDomain } from "utils";
import type { AdEngine } from "../../core/AdEngine";
import { isFanTakeoverSize } from "../TopStrategyTunerPlugin/helpers/isFanTakeoverSize";
import { DEFAULT_TOP_SLOT_NAME } from "../../helpers/takeoverConsts";
import type { AdNode } from "../../core/AdNode";
import { isMobile } from "../../helpers/isMobile";

interface InterstitialPluginAPI {
  onCeltraInterstitialImpression: () => void;
}

/**
 * Handles "google" interstitial ad functionality including:
 * - Displaying full-screen interstitial ads on link clicks
 * - Managing ad visibility and behavior
 *   - All interstitial related css styles are defined in MediaWiki (UCP) @see {@link https://github.com/Wikia/unified-platform/pull/21157/files#diff-b2b798592ad51f82426daf28f85bfe63339a9d91792bcd3a6b6744a291183ea9R157}
 *   - The above link should be updated when the css styles are merged in master
 * - Blocking page scroll during ad display
 * - Providing close button functionality
 * - Handling ad capping across different interstitial ads (google and celtra)
 */
export class InterstitialPlugin extends AdEnginePlugin<InterstitialPluginAPI> {
  private gpt: GptWrapper | undefined;

  public isPageReloaded(): boolean | undefined {
    // In case browser does not support Performance API
    if (!window.performance) return undefined;

    const navigationTiming = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming;

    return navigationTiming.type === "reload";
  }

  private setCappingCookieAndInterstitialDisabledTargeting() {
    /* Set the interstitial impression cookie, so that on the next page load,
     * the interstitial_disabled can be added to the bundles targeting KVP. */
    cookieManager.set("_ae_intrsttl_imp", "1", {
      /* This is the time in milliseconds, which is equivalent to 24 hours.
       *
       * This is calculated as:
       * 1 second = 1000 milliseconds
       * 1 minute = 60 seconds = 60,000 milliseconds
       * 1 hour = 60 minutes = 3,600,000 milliseconds
       * 24 hours = 24 × 3,600,000 = 86,400,000 milliseconds
       */
      expires: 86400000,
      /* We need to set this cookie at the .fandom.com level,
       * otherwise when we navigate between wikis, the user will be
       * eligible to see the interstitial again. This is not the intended behavior.
       *
       * This was the behavior in AdEngine 3, and we intend to keep it the same.
       * */
      domain: getTopLevelDomain(),
      path: "/",
    });
    /* Let's make sure to set the interstitial_disabled targeting in the bundles KVP
     * so that a Celtra or Google interstitial cannot be targeted again on any refresh
     * that would happen on the page.
     * Since the Celtra interstitial is served through the top_leaderboard slot at the moment,
     * there is a possibility we could get a Celtra interstitial again on a refresh if we don't set this targeting.
     * */
    this.setInterstitialDisabledTargeting();
  }

  /**
   * Sets the targeting key-value pair (KVP) to disable interstitial ads.
   *
   * This function modifies the `bundles` targeting array by adding the
   * `interstitial_disabled` value. This ensures that interstitial ads
   * (both Google and Celtra) are not eligible for rendering on subsequent
   * page loads or refreshes.
   *
   * Note - the line items that are responsible for rendering interstitial ads,
   * need to have anti-targeting for the `interstitial_disabled` value. If this
   * is not the case, then the interstitial ads can still be rendered.
   *
   * This is a bigger issue with the Celtra interstitial, since it is served through the
   * top_leaderboard slot. This function should get called after {@link Hooks.onAdEngineInit}
   * is called, so the targeting should be applied before the first ad call is made.
   */
  private setInterstitialDisabledTargeting() {
    if (this.gpt) {
      const bundles = this.gpt.getTargeting("bundles") || [];
      bundles.push("interstitial_disabled");
      this.gpt.setTargeting("bundles", bundles);
    }
  }

  /**
   * This function grabs all the <img> elements that are on the page,
   * and add the data-google-interstitial="false" attribute to them.
   * This makes the Google interstitial ad unit ineligible for being
   * triggered through them, including any ancestor <a> tag elements.
   *
   * @see {@link https://support.google.com/admanager/answer/9840201#ineligible}
   * */
  private markAnchorTagsWithImgTagsAsInterstitialIneligible(): void {
    // Grab all the <a> elements on the page that have an <img> element inside them
    for (const anchor of document.querySelectorAll("a")) {
      if (anchor.querySelector("img")) {
        // Then set the attribute on the anchor tag,to prevent
        // the Google interstitial from being triggered through them.
        anchor.setAttribute("data-google-interstitial", "false");
      }
    }
  }

  /**
   * Determines whether the interstitial ad should be loaded.
   *
   * The function checks two conditions:
   * 1. If the interstitial impression cookie (`_ae_intrsttl_imp`) is set, indicating
   *    that the user has already seen an interstitial ad within the last 24 hours.
   * 2. If the page has been reloaded, based on the browser's Performance API.
   *
   * If either condition is true, the interstitial ad is disabled by setting the
   * appropriate targeting and the function returns `false`. Otherwise, it returns `true`.
   *
   * @returns {boolean} - `true` if we're on mobile and the`_ae_intrsttl_imp` cookie is not set
   *  or the page wasn't reload , `false` otherwise.
   */
  private shouldLoadInterstitial(): boolean {
    if (cookieManager.get("_ae_intrsttl_imp") || this.isPageReloaded()) {
      this.setInterstitialDisabledTargeting();
      return false;
    }
    return isMobile();
  }

  private listenForNonFanTakeoverTopSlotRenderAndRequestInterstitial(
    adEngine: AdEngine,
    interstitialAdNode: AdNode,
    listenerForInterstitialViewableCallback: VoidFunction,
  ) {
    const cleanUpDefaultTopSlotListener =
      adEngine.gptWrapper.addSlotEventListener(
        DEFAULT_TOP_SLOT_NAME,
        "slotRenderEnded",
        (event: googletag.events.SlotRenderEndedEvent) => {
          if (!isFanTakeoverSize(event)) {
            // If there's no FanTakeover/UAP, then load in the interstitial
            // This behavior is consistent with AdEngine3, where when we had a UAP,
            // the Interstitial unit was not loaded in.
            this.markAnchorTagsWithImgTagsAsInterstitialIneligible();
            // Let's await for the requestRender to finish, before we clean up the listener.
            adEngine.requestRender([interstitialAdNode]);
            // After the request for the interstitial is sent out, let's attach impressionViewable event.
            // There's no reason to attach this listener if there was a fan takeover.
            // TODO: Maybe attach this listener should only be attached after an ad come back from the ad request.
            listenerForInterstitialViewableCallback();
          }

          // Cleanup this listener once we check whether a FanTakeover/UAP
          // was loaded in, based on the top_leaderboard slot.
          cleanUpDefaultTopSlotListener();
        },
      );
  }

  private listenForImpressionViewableOnInterstitialAndSetInterstitialImpression(
    adEngine: AdEngine,
    interstitialAdNode: AdNode,
  ) {
    const cleanupInterstitialImpressionListener =
      adEngine.gptWrapper.addSlotEventListener(
        // We're going to be using the interstitial AdNode's slotName,
        // which is an outOfPage slot name, so it will be something
        // like "gpt_unit_/5441/wka1b.IU/interstitial/smartphone/..."
        interstitialAdNode.getSlotName(),
        "impressionViewable",
        () => {
          // This is called when the interstitial ad is rendered, so we can set the capping cookie
          this.setCappingCookieAndInterstitialDisabledTargeting();
          cleanupInterstitialImpressionListener();
        },
      );
  }

  hooks: Hooks = {
    onAdEngineInit: (adEngine: AdEngine) => {
      this.gpt = adEngine.gptWrapper;
      //  This is needed so that the Celtra interstitial will be omitted from being returned
      //  in ad responses for the TLB and top_boxad. Remember, the Celtra interstitial doesn't load
      //  from the interstitial slot, but from all slots that have a pos:top in their lineItem targeting.

      // If we should not load the interstitial, we don't need to do anything else.
      if (!this.shouldLoadInterstitial()) {
        return;
      }

      // At this point, the slotName on the interstitial AdNode has already been changed
      // to an outOfPage slot name (this change happens in the GptWrapper.defineOutOfPageSlot function),
      // which looks something like "gpt_unit_/5441/wka1b.IU/interstitial/smartphone/..." instead of just 'interstitial'.
      // The getAdNodeByName method has an extra check to looks by the baseSlotName, which is still 'interstitial'.
      // This is how we're able to get the interstitial AdNode, even though the slotName has been changed.
      const interstitialAdNode = adEngine.adNodeManager.getAdNodeByName(
        "interstitial",
      ) as AdNode;

      if (!interstitialAdNode) {
        return;
      }

      this.listenForNonFanTakeoverTopSlotRenderAndRequestInterstitial(
        adEngine,
        interstitialAdNode,
        () => {
          this.listenForImpressionViewableOnInterstitialAndSetInterstitialImpression(
            adEngine,
            interstitialAdNode,
          );
        },
      );
    },
  };

  api: InterstitialPluginAPI = {
    /**
     * This is called by the Celtra interstitial creative when the ad impression occurs
     * We have a common cap on all interstitials, and we use the capping cookie to block them all
     */
    onCeltraInterstitialImpression: () => {
      this.setCappingCookieAndInterstitialDisabledTargeting();
    },
  };
}
